import { useEffect, useMemo, useRef, useState } from 'react'
import {
  cn,
  Search,
  SearchEmpty,
  SearchOptionItem,
  type SearchOption,
  type SearchProps,
  type SearchValue
} from '@jana-technology/platform-ui'

type SelectDropdownWrapperProps = SearchProps & {
  disableClear?: boolean
}

const getDefaultValue = (
  defaultValue: SearchValue | undefined,
  mode: SearchProps['mode']
): SearchValue => defaultValue ?? (mode === 'tags' ? [] : '')

const getSelectedLabel = (
  value: SearchValue,
  options: SearchOption[]
): string => {
  const selectedValue = Array.isArray(value) ? value[0] : value

  if (!selectedValue) {
    return ''
  }

  return (
    options.find((option) => option.value === selectedValue)?.label ??
    selectedValue
  )
}

const validateValueIfRoute = (
  value: SearchValue | undefined,
  mode: SearchProps['mode']
): SearchValue | undefined => {
  if (mode !== 'default') {
    return value
  }

  if (value === undefined) {
    return undefined
  }

  const numericValue = Number(value)

  return numericValue <= 0 ? '' : value
}

export const SelectDropdownWrapper = ({
  children,
  className,
  defaultOpen = false,
  defaultSearchValue = '',
  replaceChevron = true,
  defaultValue,
  mode = 'default',
  notFoundContent = 'No Data',
  onChange,
  onClear,
  onMouseDown,
  onOpenChange,
  onSearch,
  open,
  options = [],
  placeholder,
  searchValue,
  value,
  contentClassName = 'max-h-72',
  tooltipContentClassName,
  disableClear = false,
  ...props
}: SelectDropdownWrapperProps) => {
  const [internalValue, setInternalValue] = useState<SearchValue>(() =>
    getDefaultValue(defaultValue, mode)
  )
  const [internalSearchValue, setInternalSearchValue] =
    useState(defaultSearchValue)
  const [internalFilterValue, setInternalFilterValue] =
    useState(defaultSearchValue)

  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const inputMouseDownRef = useRef(false)
  const synchronizedSearchValueRef = useRef<string>()

  const currentValue = validateValueIfRoute(value, mode) ?? internalValue

  const currentSearchValue = searchValue ?? internalSearchValue
  const currentFilterValue = searchValue ?? internalFilterValue
  const currentOpen = open ?? internalOpen
  const selectedLabel = getSelectedLabel(currentValue, options)
  const hasSelection = Array.isArray(currentValue)
    ? currentValue.length > 0
    : Boolean(currentValue)

  useEffect(() => {
    if (mode === 'default' && searchValue === undefined) {
      setInternalSearchValue(selectedLabel)

      if (selectedLabel) {
        setInternalFilterValue('')
      }
    }
  }, [mode, searchValue, selectedLabel])

  const filteredOptions = useMemo(() => {
    const normalizedSearchValue = currentFilterValue.trim().toLowerCase()

    if (!normalizedSearchValue) {
      return options
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearchValue)
    )
  }, [currentFilterValue, options])

  const handleChange: NonNullable<SearchProps['onChange']> = (
    nextValue,
    option
  ) => {
    if (value === undefined) {
      setInternalValue(nextValue)
    }

    if (mode === 'default' && !Array.isArray(option)) {
      synchronizedSearchValueRef.current = option.label
    }

    onChange?.(nextValue, option)
  }

  const handleSearch = (nextSearchValue: string) => {
    if (nextSearchValue === synchronizedSearchValueRef.current) {
      synchronizedSearchValueRef.current = undefined

      if (searchValue === undefined) {
        setInternalSearchValue(nextSearchValue)
        setInternalFilterValue('')
      }

      return
    }

    if (searchValue === undefined) {
      setInternalSearchValue(nextSearchValue)
      setInternalFilterValue(nextSearchValue)
    }

    onSearch?.(nextSearchValue)
  }

  const handleTagOptionChange = (option: SearchOption) => {
    const selectedValues = Array.isArray(currentValue)
      ? currentValue
      : currentValue
        ? [currentValue]
        : []
    const nextValues = selectedValues.includes(option.value)
      ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
      : [...selectedValues, option.value]
    const nextOptions = nextValues.map(
      (nextValue) =>
        options.find(({ value: optionValue }) => optionValue === nextValue) ?? {
          label: nextValue,
          value: nextValue
        }
    )

    handleChange(nextValues, nextOptions)
    handleSearch('')
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && inputMouseDownRef.current) {
      return
    }

    if (open === undefined) {
      setInternalOpen(nextOpen)
    }

    onOpenChange?.(nextOpen)
  }

  const handleMouseDown: NonNullable<SearchProps['onMouseDown']> = (event) => {
    inputMouseDownRef.current = true

    setTimeout(() => {
      inputMouseDownRef.current = false
    }, 0)

    if (!currentOpen) {
      event.preventDefault()
    }

    onMouseDown?.(event)
  }

  const handleClear = () => {
    if (value === undefined) {
      setInternalValue(getDefaultValue(undefined, mode))
    }

    if (searchValue === undefined) {
      setInternalSearchValue('')
      setInternalFilterValue('')
    }

    onClear?.()
  }

  // z-[1500] prevents the MUI modal overlapping the dropdown, which has a z-index of 1300
  // refactor this later to pass a modal content node to contentContainer

  const tagDropdownContent =
    filteredOptions.length > 0 ? (
      <div role='listbox'>
        {filteredOptions.map((option) => (
          <SearchOptionItem
            checked={
              Array.isArray(currentValue) && currentValue.includes(option.value)
            }
            disabled={option.disabled}
            key={option.value}
            label={option.label}
            onCheckedChange={() => handleTagOptionChange(option)}
          />
        ))}
      </div>
    ) : (
      <SearchEmpty>{notFoundContent}</SearchEmpty>
    )
  const dropdownContent =
    children ?? (mode === 'tags' ? tagDropdownContent : undefined)

  return (
    <div className={cn('relative', className)}>
      <Search
        {...props}
        className='h-full w-full'
        contentClassName={cn('z-[1500]', contentClassName)}
        tooltipContentClassName={cn('z-[1500]', tooltipContentClassName)}
        mode={mode}
        onChange={handleChange}
        onClear={disableClear ? undefined : handleClear}
        onMouseDown={handleMouseDown}
        onOpenChange={handleOpenChange}
        onSearch={handleSearch}
        open={currentOpen}
        options={mode === 'tags' ? options : filteredOptions}
        placeholder={hasSelection ? '' : placeholder}
        searchValue={currentSearchValue}
        value={currentValue}
        replaceChevron={replaceChevron}
        notFoundContent={notFoundContent}
        {...(dropdownContent === undefined
          ? {}
          : { children: dropdownContent })}
      />
    </div>
  )
}
